import React, { useCallback } from 'react';
import {
	Bold,
	Italic,
	Underline,
	Strike,
	H1,
	H2,
	H3,
	ListUnordered,
	ListOrdered,
	AlignLeft,
	AlignCenter,
	AlignRight,
	AlignJustify,
	Link2,
	TextColor,
	Separator,
	Superscript,
	Subscript,
	ClearFormat,
} from '@blueprintnyc/bpnyc-common-admin/dist/icons';
// import ColorInput from '../ColorInput';

const IconWrapper = ({ children, content }) => {
	return <span title={content}>{children}</span>;
};

const MenuBar = ({ editor }) => {
	// const [showColorPicker, setShowColorPicker] = useState(false);
	if (!editor) return null;

	const handleColorChange = color => {
		editor
			.chain()
			.focus()
			.setColor(color)
			.updateAttributes('listItem', { style: `color: ${color}` })
			.run();
	};

	const setLink = useCallback(() => {
		const previousUrl = editor.getAttributes('link').href;
		const url = window.prompt('URL', previousUrl);

		if (url === null) return;
		if (url === '') {
			editor
				.chain()
				.focus()
				.extendMarkRange('link')
				.unsetLink()
				.run();

			return;
		} else
			editor
				.chain()
				.focus()
				.extendMarkRange('link')
				.setLink({ href: url })
				.setColor('#0c6aae') // link color
				.run();
	}, [editor]);

	const buttons = [
		{
			label: (
				<IconWrapper content={'Bold'}>
					<Bold />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.toggleBold()
					.run(),
			isActive: () => editor.isActive('bold'),
		},
		{
			label: (
				<IconWrapper content={'Italic'}>
					<Italic />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.toggleItalic()
					.run(),
			isActive: () => editor.isActive('italic'),
		},
		{
			label: (
				<IconWrapper content={'Underline'}>
					<Underline />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.toggleUnderline()
					.run(),
			isActive: () => editor.isActive('underline'),
		},
		{
			label: (
				<IconWrapper content={'Strike'}>
					<Strike />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.toggleStrike()
					.run(),
			isActive: () => editor.isActive('strike'),
		},
		{
			label: (
				<IconWrapper content={'Heading 1'}>
					<H1 />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.toggleHeading({ level: 1 })
					.run(),
			isActive: () => editor.isActive('heading', { level: 1 }),
		},
		{
			label: (
				<IconWrapper content={'Heading 2'}>
					<H2 />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.toggleHeading({ level: 2 })
					.run(),
			isActive: () => editor.isActive('heading', { level: 2 }),
		},
		{
			label: (
				<IconWrapper content={'Heading 3'}>
					<H3 />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.toggleHeading({ level: 3 })
					.run(),
			isActive: () => editor.isActive('heading', { level: 3 }),
		},
		{
			label: (
				<IconWrapper content={'Superscript'}>
					<Superscript />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.toggleSuperscript()
					.run(),
			isActive: () => editor.isActive('superscript'),
		},
		{
			label: (
				<IconWrapper content={'Subscript'}>
					<Subscript />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.toggleSubscript()
					.run(),
			isActive: () => editor.isActive('subscript'),
		},
		{
			label: (
				<IconWrapper content={'Bullet List'}>
					<ListUnordered />
				</IconWrapper>
			),
			command: () => {
				const color = editor.getAttributes('textStyle')?.color || 'black';
				return editor
					.chain()
					.focus()
					.toggleBulletList()
					.updateAttributes('listItem', { style: `color: ${color}` })
					.run();
			},
			isActive: () => editor.isActive('bulletList'),
		},
		{
			label: (
				<IconWrapper content={'Number List'}>
					<ListOrdered />
				</IconWrapper>
			),
			command: () => {
				const color = editor.getAttributes('textStyle')?.color || 'black';
				return editor
					.chain()
					.focus()
					.toggleOrderedList()
					.updateAttributes('listItem', { style: `color: ${color}` })
					.run();
			},
			isActive: () => editor.isActive('orderedList'),
		},
		{
			label: (
				<IconWrapper content={'Align Left'}>
					<AlignLeft />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.setTextAlign('left')
					.run(),
			isActive: () => editor.isActive({ textAlign: 'left' }),
		},
		{
			label: (
				<IconWrapper content={'Align Center'}>
					<AlignCenter />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.setTextAlign('center')
					.run(),
			isActive: () => editor.isActive({ textAlign: 'center' }),
		},
		{
			label: (
				<IconWrapper content={'Align Right'}>
					<AlignRight />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.setTextAlign('right')
					.run(),
			isActive: () => editor.isActive({ textAlign: 'right' }),
		},
		{
			label: (
				<IconWrapper content={'Align Justify'}>
					<AlignJustify />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.setTextAlign('justify')
					.run(),
			isActive: () => editor.isActive({ textAlign: 'justify' }),
		},
		{
			label: (
				<IconWrapper content={'Hyperlink'}>
					<Link2 />
				</IconWrapper>
			),
			command: setLink,
			isActive: () => editor.isActive('link'),
		},
		{
			label: (
				<IconWrapper content={'Separator'}>
					<Separator />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.setHorizontalRule()
					.run(),
			isActive: () => {},
		},
		{
			label: (
				<IconWrapper content={'Clear Format'}>
					<ClearFormat />
				</IconWrapper>
			),
			command: () =>
				editor
					.chain()
					.focus()
					.clearNodes()
					.unsetAllMarks()
					.run(),
			isActive: () => {},
		},
	];

	const textColor = editor.getAttributes('textStyle').color;

	return (
		<div className="MenuBar">
			<div className="toolbar">
				{/* Text Color Picker */}
				<div className="text-color">
					<input
						type="color"
						id="color-input"
						value={textColor}
						onChange={e => handleColorChange(e.target.value)}
					/>
					<label
						className="menu-button"
						htmlFor="color-input"
						style={{ color: textColor === '#ffffff' ? 'black' : textColor }}
					>
						<IconWrapper content={'Text Color'}>
							<TextColor />
						</IconWrapper>
					</label>
				</div>
				{buttons.map((button, index) => (
					<button
						key={index}
						onMouseDown={e => {
							e.preventDefault();
							button.command();
						}}
						className="menu-button"
						style={{
							backgroundColor: button.isActive() ? '#e0f2ff' : '#fff',
							color: button.isActive() ? '#00108e' : '#000',
						}}
					>
						{button.label}
					</button>
				))}
				{/* <button
					key={'color-picker'}
					onMouseDown={e => {
						e.preventDefault();
						setShowColorPicker(!showColorPicker);
					}}
					className="menu-button"
				>
					<TextColor fill={editor.getAttributes('textStyle').color} />
				</button> */}
			</div>
			{/* {showColorPicker ? (
				<ColorInput
					value={editor.getAttributes('textStyle').color || '#000000'}
					onChange={hex => handleColorChange(hex)}
				/>
			) : null} */}
		</div>
	);
};

export default MenuBar;
